/*
 * The gunbarrel tab's two viewers, each wrapped so that it reads its own saved
 * state as IT mounts.
 *
 * The view switcher writes to the panel store, which does not unmount the tab —
 * it only swaps which branch the tab renders. So the two viewers mount and
 * unmount many times over a tab's life, and a snapshot taken once when the tab
 * mounted is stale from the first switch onwards: come back to a viewer whose
 * settings you changed and it rebuilds from the old value, or from nothing.
 * Worse, its persist effect then writes those freshly-defaulted options over
 * the stored key, so the change is lost from storage too and every later switch
 * restores defaults as well.
 *
 * A wrapper per branch is what puts the read on the viewer's own mount. It has
 * to be a component rather than a call in the tab, because only a component
 * that mounts and unmounts with the branch has a mount to read at.
 */
import { useState } from "react";

import { ErrorBoundary } from "components/base";
import { IpdbStoreProvider } from "components/map/contexts/IpdbStoreProvider";
import { ThreeDViewer, XdaSettingsStoreProvider, XdaViewer } from "components/vis";

import {
  WELL_CARD_PANEL_THREE_D_ID,
  WELL_CARD_PANEL_THREE_D_SCREENSHOT_ID,
  WELL_CARD_PANEL_XDA_ID,
  WELL_CARD_PANEL_XDA_SCREENSHOT_ID,
  WELL_CARD_PANEL_XDA_TOOLTIP_ID,
  WELL_CARD_PANEL_XDA_TYPE
} from "../constants";
import { readThreeDSessionState, readXdaSessionState } from "../utils/viewerSessionState";

const noop = () => undefined;

interface GunbarrelViewerProps {
  /** Null while the card's fullscreen sheet is up, so the viewer undocks. */
  toolbarContainer: Maybe<HTMLDivElement>;
  screenshotWidgetId: string;
}

export interface GunbarrelXdaViewerProps extends GunbarrelViewerProps {
  /** The card's own well, which the section measures spacing from. */
  referenceUwi: Maybe<string>;
}

// The card sits outside every dashboard widget's error boundary, so a viewer
// crash would take down the whole app. The boundary sits above the saved-state
// read so Retry re-reads storage instead of restoring a stale snapshot.
export function GunbarrelXdaViewer(props: GunbarrelXdaViewerProps): JSX.Element {
  return (
    <ErrorBoundary>
      <XdaViewerBody {...props} />
    </ErrorBoundary>
  );
}

function XdaViewerBody({
  toolbarContainer,
  screenshotWidgetId,
  referenceUwi
}: GunbarrelXdaViewerProps): JSX.Element {
  // Read once per mount of THIS component, which is once per switch back to the
  // cross-section. The settings store snapshots what it is handed and ignores
  // it afterwards, so re-reading on every render would be work with no effect.
  const [initialState] = useState(readXdaSessionState);

  return (
    <IpdbStoreProvider>
      <XdaSettingsStoreProvider initialState={initialState}>
        <XdaViewer
          id={WELL_CARD_PANEL_XDA_ID}
          widgetType={WELL_CARD_PANEL_XDA_TYPE}
          onFullscreenToggle={noop}
          hideFullscreen
          toolbarContainer={toolbarContainer}
          referenceUwi={referenceUwi}
          /* The card's section belongs to the card's well. The wells it holds
             are the ones the derive found crossing the line, so a bare map
             click — which selects a well for the rest of the dashboard — must
             not reach into it. */
          sectionFromLineOnly
          screenshotContainerId={WELL_CARD_PANEL_XDA_SCREENSHOT_ID}
          screenshotWidgetId={screenshotWidgetId}
          tooltipId={WELL_CARD_PANEL_XDA_TOOLTIP_ID}
        />
      </XdaSettingsStoreProvider>
    </IpdbStoreProvider>
  );
}

export function GunbarrelThreeDViewer(props: GunbarrelViewerProps): JSX.Element {
  return (
    <ErrorBoundary>
      <ThreeDViewerBody {...props} />
    </ErrorBoundary>
  );
}

function ThreeDViewerBody({
  toolbarContainer,
  screenshotWidgetId
}: GunbarrelViewerProps): JSX.Element {
  // Same rule as the cross-section above: read at this component's mount.
  const [initialState] = useState(readThreeDSessionState);

  return (
    /* This provider is left bare on purpose: the 3D viewer does not read the
       XDA settings store, and the settings it does own are the options it is
       handed below. */
    <XdaSettingsStoreProvider>
      <ThreeDViewer
        id={WELL_CARD_PANEL_THREE_D_ID}
        initialState={initialState}
        onFullscreenToggle={noop}
        hideFullscreen
        toolbarContainer={toolbarContainer}
        screenshotContainerId={WELL_CARD_PANEL_THREE_D_SCREENSHOT_ID}
        screenshotWidgetId={screenshotWidgetId}
      />
    </XdaSettingsStoreProvider>
  );
}
